import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as echarts from 'echarts';
import { Subscription } from 'rxjs';

import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { SelfServiceFormulaService } from '../../self-service-visualization/shared/self-service-formula.service';
import { buildBarColumnOption } from './unified-bar-column-option.builder';
import type { BarColumnWidgetData } from './unified-bar-column-chart.types';

/**
 * Unified Bar & Column chart — ONE ECharts component for the whole family
 * (Simple / Grouped / Stacked / 100% Stacked / Clustered) in both orientations
 * (vertical columns / horizontal bars), plus Line/Area combos and dual axes.
 *
 * All variation lives in `data.capabilities` (see BarColumnCapabilities); the
 * PURE `buildBarColumnOption` turns capabilities + flat rows into the option and
 * this component is just the lifecycle shell (init / dispose / resize / theme),
 * mirroring the other shared bar components. Reads pre-fetched rows from
 * `staticData` / `rows` / `data` (the DVM Layout Builder passes dataset rows
 * directly) or self-fetches via ConfigService.getConfig when only `apiUrl` is set.
 */
@Component({
  selector: 'app-unified-bar-column-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './unified-bar-column-chart.component.html',
  styleUrl: './unified-bar-column-chart.component.scss',
})
export class UnifiedBarColumnChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  // Accepts a plain record too — hosts (DVM Layout Builder tiles, published
  // renderer) pass an untyped `data` object; the component reads capabilities /
  // rows defensively, so a loose input avoids friction at every binding site.
  @Input() data!: BarColumnWidgetData | Record<string, unknown>;

  /** Emitted on bar click when `capabilities.emitSelection` is on — carries the
   *  clicked category, series (if split), and value. Hosts can react (e.g. drive
   *  another panel) without the component knowing about them. */
  @Output() selection = new EventEmitter<{ category: string; series?: string; value: number }>();

  /** Emitted on bar click when `capabilities.crossFilter` is on — the column +
   *  the FULL set of currently-selected values (multi-select: each click toggles
   *  a value in/out). `value` is the last-toggled value for back-compat; `values`
   *  is the complete selection (empty array = nothing selected → clear the
   *  filter). The DVM builder re-filters every OTHER tile's rows by column IN
   *  values (client-side). */
  @Output() crossFilterChange = new EventEmitter<{ column: string; value: string | null; values: string[] }>();

  protected myChart!: echarts.ECharts;
  /** Categories currently emphasised via highlight-on-click (empty = none). */
  private highlighted = new Set<string>();
  /** The categories currently driving the cross-filter — multi-select. Each bar
   *  click toggles its category in/out of this set. */
  private crossFilterValues = new Set<string>();

  /** The ECharts host. Preferred over getElementById(widgetId) so the chart works
   *  even when two tiles briefly share a stale widgetId during a re-render. */
  @ViewChild('ubcHost', { static: false }) private hostRef?: ElementRef<HTMLDivElement>;

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private formulaService = inject(SelfServiceFormulaService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private resizeObserver?: ResizeObserver;
  private lastRows: Array<Record<string, unknown>> = [];

  /** Typed view of the loose `data` input. */
  private get cfg(): BarColumnWidgetData {
    return (this.data || {}) as BarColumnWidgetData;
  }

  /** CSS height for the host div. The builder passes '100%'; the PUBLISHED view
   *  passes a raw NUMBER (e.g. 320) — a bare number is an invalid CSS height and
   *  collapses the host to 0px, so ECharts paints nothing (blank published tile).
   *  Coerce numbers (and numeric strings) to px; pass through valid CSS strings. */
  get hostHeight(): string {
    const h = this.cfg.height;
    if (h === undefined || h === null || h === '') return 'var(--dvm-chart-height, 300px)';
    if (typeof h === 'number') return `${h}px`;
    return /^\d+$/.test(String(h)) ? `${h}px` : String(h);
  }

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => [],
      getCategories: () => [],
    });

    const staticRows = this.resolveStaticRows();
    if (staticRows) {
      setTimeout(() => this.renderRows(staticRows), 0);
    } else {
      setTimeout(() => this.loadAndRenderChart(initialFilters ?? undefined), 0);
    }

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // The DVM tile lays out (flex height, drag-resize) AFTER the chart inits, so
    // ECharts would otherwise measure a 0-height box and paint nothing. Observe
    // the host and re-fit whenever its size changes (same fix chart-preview uses).
    const host = this.resolveHost();
    if (host && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.myChart?.resize());
      this.resizeObserver.observe(host);
    }

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init is required because ECharts' registered 'dvm' theme
    // updates do NOT propagate to already-initialized chart instances.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.myChart || !this.lastRows.length) return;
      const chartDom = this.resolveHost();
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderRows(this.lastRows);
    });
  }

  /** Re-render when the host swaps in a new `data` object (e.g. the DVM builder
   *  changed capabilities/format like Sort, or applied a cross-filter to the
   *  rows). Without this the chart would only ever paint once in ngAfterViewInit,
   *  so a Format/Sort change on an existing tile wouldn't take effect unless the
   *  tile happened to be fully remounted. */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data'] || changes['data'].isFirstChange()) return;
    if (!this.myChart) return; // not initialised yet — ngAfterViewInit will render
    const staticRows = this.resolveStaticRows();
    this.renderRows(staticRows ?? this.lastRows);
  }

  /** The host div — the ViewChild ref, falling back to the widgetId lookup. */
  private resolveHost(): HTMLElement | null {
    return this.hostRef?.nativeElement || document.getElementById(this.cfg.widgetId || '');
  }

  /** Pre-fetched rows a host has already supplied (DVM Layout Builder path). */
  private resolveStaticRows(): Array<Record<string, unknown>> | null {
    const candidate = this.cfg.staticData ?? this.cfg.rows ?? this.cfg.data;
    if (Array.isArray(candidate)) return candidate as Array<Record<string, unknown>>;
    // `data.data` may itself wrap the rows array.
    const nested = (candidate as { data?: unknown })?.data;
    return Array.isArray(nested) ? (nested as Array<Record<string, unknown>>) : null;
  }

  protected loadAndRenderChart(filters?: unknown): void {
    const chartDom = this.resolveHost();
    if (!chartDom) return;
    if (!this.myChart) this.myChart = echarts.init(chartDom, 'dvm');

    const staticRows = this.resolveStaticRows();
    if (staticRows) {
      this.renderRows(staticRows);
      return;
    }
    if (!this.cfg.apiUrl) {
      this.renderRows([]);
      return;
    }

    this.myChart.showLoading();
    this.configService.getConfig(this.cfg.apiUrl, this.data, filters)?.subscribe({
      next: (res: unknown) => {
        const raw = (res as { data?: unknown })?.data ?? res;
        this.renderRows(Array.isArray(raw) ? (raw as Array<Record<string, unknown>>) : []);
        this.myChart.hideLoading();
      },
      error: () => this.myChart.hideLoading(),
    });
  }

  /** Re-derive formula columns onto the rows — refetched rows arrive without
   *  them, so a formula-driven axis/measure would render blank. */
  private applyComputedColumns(rows: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
    const computedColumns = this.cfg.capabilities?.computedColumns;
    if (!computedColumns?.length || !rows.length) return rows;

    const known = Object.keys(rows[0] ?? {});
    let working = rows;
    for (const computed of computedColumns) {
      if (!computed?.name || !computed?.expression) continue;
      try {
        const evaluate = this.formulaService.buildRowEvaluator(computed.expression, known);
        working = working.map((row) => {
          const value = evaluate(row as Record<string, string>);
          const resolved = value === null || value === undefined ? '' : value;
          return {
            ...row,
            [computed.name]: typeof resolved === 'number' && !Number.isFinite(resolved) ? '' : resolved,
          };
        });
        known.push(computed.name);
      } catch {
        // Skip a broken formula rather than blanking the chart.
      }
    }
    return working;
  }

  private renderRows(rows: Array<Record<string, unknown>>): void {
    const capabilities = this.cfg.capabilities;
    if (!capabilities) return;
    // Keep RAW rows: re-renders re-derive rather than compounding.
    this.lastRows = rows;

    const chartDom = this.resolveHost();
    if (!chartDom) return;
    if (!this.myChart) this.myChart = echarts.init(chartDom, 'dvm');

    const option = buildBarColumnOption(this.applyComputedColumns(rows), capabilities) as Record<string, unknown>;
    applyRoleVisibilityToOption(option, this.data);
    this.myChart.setOption(option, true);
    this.myChart.hideLoading();
    // The tile height often resolves AFTER init — resize now, and again on the
    // next frame, so the canvas fills the tile instead of painting at 0px.
    this.myChart.resize();
    setTimeout(() => this.myChart?.resize(), 60);
    this.addResizeListener();
    // IMPORTANT: run afterRender() FIRST — the ChartWrapperService's afterRender
    // does `chart.off('click')` and attaches its OWN click handler. If we set up
    // our interactions before it, the wrapper wipes our cross-filter/highlight
    // click handler and nothing happens on click. Running ours LAST wins.
    this.wrapper.afterRender();
    // The wrapper's `legendselectchanged` handler REVERTS every legend toggle
    // unless `enableLegendClick` cross-chart filtering is configured — which
    // this chart never uses (its getSeriesNames() is empty), so legend clicks
    // looked dead. Drop that handler to restore ECharts' native show/hide.
    this.myChart.off('legendselectchanged');
    this.setupInteractions();
  }

  /** Wire the Interaction capabilities that need a live click handler
   *  (highlight-on-click, cross-filter, emit-selection). No-op when none are on. */
  private setupInteractions(): void {
    const cap = this.cfg.capabilities;
    if (!cap || !this.myChart) return;
    const anyClick = cap.highlightOnClick || cap.crossFilter || cap.emitSelection;
    if (!anyClick) return; // leave the wrapper's handler in place when we add none
    this.myChart.off('click');

    this.myChart.on('click', (params: { seriesType?: string; name?: string; seriesName?: string; value?: unknown }) => {
      // Only react to data-item clicks (bars/lines), not axis/legend.
      if (params.seriesType !== 'bar' && params.seriesType !== 'line') return;
      const category = String(params.name ?? '');
      const value = Number(params.value ?? 0);

      if (cap.emitSelection) {
        this.selection.emit({ category, series: cap.series ? params.seriesName : undefined, value });
      }
      if (cap.highlightOnClick) {
        this.toggleHighlight(category);
      }
      if (cap.crossFilter) {
        // Multi-select: toggle this category in/out of the selection set, then
        // emit the FULL set. Clicking a selected bar again removes it; clicking a
        // new bar adds it. The DVM builder filters OTHER tiles by column IN values.
        const column = cap.crossFilterKey || cap.category;
        if (this.crossFilterValues.has(category)) this.crossFilterValues.delete(category);
        else this.crossFilterValues.add(category);
        const values = Array.from(this.crossFilterValues);
        // Reflect the selection in-chart so the user sees which bars are picked.
        this.applyCrossFilterEmphasis();
        this.crossFilterChange.emit({ column, value: category, values });
      }
    });
  }

  /** Toggle a category's emphasis: clicked stays vivid, the rest dim. */
  private toggleHighlight(category: string): void {
    if (this.highlighted.has(category)) this.highlighted.delete(category);
    else this.highlighted.add(category);
    // Downplay everything, then re-highlight the selected categories' bars by
    // their data index across all series.
    this.myChart.dispatchAction({ type: 'downplay' });
    if (this.highlighted.size > 0) {
      const opt = this.myChart.getOption() as { xAxis?: Array<{ data?: string[] }>; yAxis?: Array<{ data?: string[] }> };
      const horizontal = this.cfg.capabilities?.orientation === 'horizontal';
      const axisData = (horizontal ? opt.yAxis?.[0]?.data : opt.xAxis?.[0]?.data) || [];
      axisData.forEach((name, dataIndex) => {
        if (this.highlighted.has(String(name))) {
          this.myChart.dispatchAction({ type: 'highlight', dataIndex });
        }
      });
    }
  }

  /** Emphasise the multi-selected cross-filter bars (selected stay vivid, the
   *  rest dim) so the user can see which categories are picked. Clears emphasis
   *  when nothing is selected. */
  private applyCrossFilterEmphasis(): void {
    if (!this.myChart) return;
    this.myChart.dispatchAction({ type: 'downplay' });
    if (this.crossFilterValues.size === 0) return;
    const opt = this.myChart.getOption() as { xAxis?: Array<{ data?: string[] }>; yAxis?: Array<{ data?: string[] }> };
    const horizontal = this.cfg.capabilities?.orientation === 'horizontal';
    const axisData = (horizontal ? opt.yAxis?.[0]?.data : opt.xAxis?.[0]?.data) || [];
    axisData.forEach((name, dataIndex) => {
      if (this.crossFilterValues.has(String(name))) {
        this.myChart.dispatchAction({ type: 'highlight', dataIndex });
      }
    });
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}
